import { ApiModel } from '@composable-api/api.model'
import type { ApiDateTime, FormattedCurrency, TaxPerRate } from '../types/general-data'
import { RefundShippingMethodModel } from '@simploshop-models/refund-shipping-method.model'
import type { GetPriceOptions } from '@simploshop-models/product.model'
import { getFormattedPrice } from '../utils/localization'

interface Attributes {
    [RefundModel.ATTR_ID]: string
    [RefundModel.ATTR_IS_WITHDRAWAL]: boolean
    [RefundModel.ATTR_TOTAL_AMOUNT]: number
    [RefundModel.ATTR_UNIQUE_AMOUNT]: number
    [RefundModel.ATTR_TOTAL_PRICE]: FormattedCurrency
    [RefundModel.ATTR_TAXED_TOTAL_PRICE]: FormattedCurrency
    [RefundModel.ATTR_TAX_PER_RATE]: TaxPerRate
    [RefundModel.ATTR_TAX_AMOUNT]: FormattedCurrency
    [RefundModel.ATTR_SHIPPING_METHOD]: RefundShippingMethodModel | null
    [RefundModel.ATTR_HAS_SHIPPING_ADDRESS]: boolean
    [RefundModel.ATTR_EXPIRATION_AT]: ApiDateTime
}

interface Embeds {
}

export class RefundModel extends ApiModel<Attributes, Embeds> {
    static key = 'RefundModel'

    static readonly ATTR_ID = 'id'
    static readonly ATTR_IS_WITHDRAWAL = 'is_withdrawal'
    static readonly ATTR_TOTAL_AMOUNT = 'total_amount'
    static readonly ATTR_UNIQUE_AMOUNT = 'unique_amount'
    static readonly ATTR_TOTAL_PRICE = 'total_price'
    static readonly ATTR_TAXED_TOTAL_PRICE = 'taxed_total_price'
    static readonly ATTR_TAX_PER_RATE = 'tax_per_rate'
    static readonly ATTR_TAX_AMOUNT = 'tax_amount'
    static readonly ATTR_SHIPPING_METHOD = 'shipping_method'
    static readonly ATTR_HAS_SHIPPING_ADDRESS = 'has_shipping_address'
    static readonly ATTR_EXPIRATION_AT = 'expiration_at'

    get id() {
        return this._getAttribute(RefundModel.ATTR_ID)
    }

    get isWithdrawal() {
        return this._getAttribute(RefundModel.ATTR_IS_WITHDRAWAL)
    }

    get totalAmount() {
        return this._getAttribute(RefundModel.ATTR_TOTAL_AMOUNT)
    }

    get uniqueAmount() {
        return this._getAttribute(RefundModel.ATTR_UNIQUE_AMOUNT)
    }

    private get totalPrice() {
        return this._getAttribute(RefundModel.ATTR_TOTAL_PRICE)
    }

    private get taxedTotalPrice() {
        return this._getAttribute(RefundModel.ATTR_TAXED_TOTAL_PRICE)
    }

    get taxPerRate() {
        return this._getAttribute(RefundModel.ATTR_TAX_PER_RATE)
    }

    get taxAmount() {
        return this._getAttribute(RefundModel.ATTR_TAX_AMOUNT)
    }

    get shippingMethod() {
        return this._getAttribute(RefundModel.ATTR_SHIPPING_METHOD, RefundShippingMethodModel)
    }

    set shippingMethod(value) {
        this._setAttribute(RefundModel.ATTR_SHIPPING_METHOD, value)
    }

    get hasShippingAddress() {
        return this._getAttribute(RefundModel.ATTR_HAS_SHIPPING_ADDRESS)
    }

    get expirationAt() {
        return this._getAttribute(RefundModel.ATTR_EXPIRATION_AT)
    }

    getTotalPrice(options?: Partial<GetPriceOptions>) {
        return (options?.tax !== false
            ? this.taxedTotalPrice
            : this.totalPrice) ?? null
    }

    getTotalFormattedPrice(options?: Partial<GetPriceOptions>) {
        return getFormattedPrice(this.getTotalPrice(options))
    }
}
